import React from 'react';
import { XMarkIcon } from '@heroicons/react/24/outline';

interface JsonPreviewOverlayProps {
  isOpen: boolean;
  onClose: () => void;
  jsonData: any;
  title?: string;
  loading?: boolean;
  error?: string;
}

const JsonPreviewOverlay: React.FC<JsonPreviewOverlayProps> = ({
  isOpen,
  onClose,
  jsonData,
  title = "Profiling Rules JSON",
  loading = false,
  error
}) => {
  if (!isOpen) return null;

  const handleOverlayClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const formatJson = (data: any) => {
    try {
      return JSON.stringify(data, null, 2);
    } catch (err) {
      return 'Invalid JSON data';
    }
  };

  return (
    <div 
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
      onClick={handleOverlayClick}
    >
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h2 className="text-xl font-semibold text-gray-900">{title}</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <XMarkIcon className="w-6 h-6" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-auto">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
              <span className="ml-3 text-gray-600">Loading profiling rules...</span>
            </div>
          ) : error ? (
            <div className="flex items-center justify-center h-64">
              <div className="text-center">
                <div className="text-red-500 text-lg font-medium mb-2">Error</div>
                <div className="text-gray-600">{error}</div>
              </div>
            </div>
          ) : (
            <div className="h-full overflow-auto p-6">
              <pre className="text-sm text-gray-800 font-mono whitespace-pre-wrap break-words leading-relaxed">
                {formatJson(jsonData)}
              </pre>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex justify-end p-6 border-t border-gray-200">
          <button
            onClick={onClose}
            className="px-4 py-2 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default JsonPreviewOverlay;
